import React, {useEffect} from 'react';
import {
    Control,
    FormProvider,
    UseFormRegister,
    UseFormSetValue,
    useFieldArray,
    useForm,
    useFormContext,
    useWatch,
} from 'react-hook-form';
import {
    CheckboxField,
    DateField,
    FieldGrid,
    NumberField,
    TextAreaField,
    TextField,
    CurrencyField,
    ToggleField
} from '../../../../ui/form/fields';
import {createDefaultDocumentacionAdjudicatario} from '../projectFormUtils';
import {
    buildCardTitle,
    buildSummary,
    EmptyState,
    formatSummaryNumber,
    FormSection,
    getStatusTone,
    hasPersistedId,
    ItemCard,
    NestedPanel,
    StatusPill,
    useProjectReadOnly,
    useProjectPublicView,
    BulkToggle
} from '../projectForm.helpers';
import {useCrudSection} from '../useCrudSection';
import { normalizeProyectoForm, prepareProyectoPayload } from '../projectFormUtils';
import type {ProyectoAdjudicatarioDTO, ProyectoFormDTO, ProyectoDatosContratosDTO} from '../../../../api/Objetos';
import {contratosPublicosApi, datosContratosApi} from "../../../../api/api";
import {useRegisterSectionSave} from "../SectionSaveCoordinator";
import {SectionAlerts, SectionSaveButton} from "../sectionCrudUtils";
import BarraCargando from "../../../../ui/BarraCargando";
import { useTranslation } from 'react-i18next';
import {DocumentosAdjuntosPanel} from '../DocumentosAdjuntosPanel';

// Componente unificado para Datos de Contratos
export function DatosContratosSection(props: { proyectoId: number; showIds?: boolean }) {
    const publicView = useProjectPublicView();
    const readOnly = useProjectReadOnly();
    const form = useForm<ProyectoFormDTO>({
        defaultValues: {
            datosContratos: {
                contratoMayores: [],
                contratoMenores: [],
                gastosImputadosFueraDeLaSubvencion: [],
            },
        },
    });

    const section = useCrudSection({
        queryKey: [publicView ? 'public' : 'admin', 'proyectos', 'datos-contratos'],
        proyectoId: props.proyectoId,
        getFn: publicView ? getPublicContracts : datosContratosApi.get,
        saveFn: datosContratosApi.save,
    });

    useEffect(() => {
        if (section.data) {
            const normalizedData = normalizeProyectoForm({ datosContratos: section.data });
            form.reset(normalizedData);
        }
    }, [form, section.data]);

    const handleSave = form.handleSubmit(async (value) => {
        try {
            const payload = prepareProyectoPayload(value);
            await section.save(payload.datosContratos ?? {});
        } catch (error) {
            console.error("Error al guardar contratos:", error);
            window.alert("Error al intentar guardar los cambios.");
        }
    });

    useRegisterSectionSave({
        id: 'contratos',
        isDirty: form.formState.isDirty,
        isSaving: section.saveMutation.isPending,
        save: async () => {
            await handleSave();
        },
    });

    return (
        <FormProvider {...form}>
            <ContratosSectionFields 
                proyectoId={props.proyectoId}
                showIds={Boolean(props.showIds)}
                isLoading={section.isLoading}
                loadError={section.error}
            />
            {!readOnly && (
                <>
                    <SectionSaveButton onClick={() => void handleSave()} busy={section.saveMutation.isPending}/>
                </>
            )}
        </FormProvider>
    );
}

async function getPublicContracts(proyectoId: number): Promise<ProyectoDatosContratosDTO | null> {
    const data = await contratosPublicosApi.listar(proyectoId);
    return {
        id: data.id,
        publico: true,
        contratoMayores: (data.contratosMayores ?? []).map(contrato => ({
            ...contrato,
            adjudicatario: undefined,
            proyectoSubcontratistas: [],
            publico: true,
        })),
        contratoMenores: [],
        gastosImputadosFueraDeLaSubvencion: [],
    };
}

function ContratosSectionFields(props: { proyectoId: number; showIds: boolean, isLoading: boolean, loadError: any }) {
    const { t } = useTranslation();
    const form = useFormContext<ProyectoFormDTO>();
    const readOnly = useProjectReadOnly();

    const contratosMayores = useFieldArray({control: form.control, name: 'datosContratos.contratoMayores'});
    const contratosMenores = useFieldArray({control: form.control, name: 'datosContratos.contratoMenores'});
    const gastos = useFieldArray({control: form.control, name: 'datosContratos.gastosImputadosFueraDeLaSubvencion'});
    const showId = (path: string) => props.showIds && hasPersistedId(form.watch(path as any));

    const setAllContratosMayoresPublic = (isPublic: boolean) => {
        contratosMayores.fields.forEach((field, index) => {
            form.setValue(`datosContratos.contratoMayores.${index}.publico`, isPublic, { shouldDirty: true });
        });
    };

    const setAllContratosMenoresPublic = (isPublic: boolean) => {
        contratosMenores.fields.forEach((field, index) => {
            form.setValue(`datosContratos.contratoMenores.${index}.publico`, isPublic, { shouldDirty: true });
        });
    };

    const setAllGastosPublic = (isPublic: boolean) => {
        gastos.fields.forEach((field, index) => {
            form.setValue(`datosContratos.gastosImputadosFueraDeLaSubvencion.${index}.publico`, isPublic, { shouldDirty: true });
        });
    };

    const currentContratosMayores = form.watch('datosContratos.contratoMayores') || [];
    const currentContratosMenores = form.watch('datosContratos.contratoMenores') || [];
    const currentGastos = form.watch('datosContratos.gastosImputadosFueraDeLaSubvencion') || [];

    return (
        <FormSection
            id="contratos"
            title={t('datosContratosSection.title')}
            collapsible
            defaultOpen={false}
            headerActions={!readOnly &&
                <div style={{display: 'flex', alignItems: 'center', gap: '1rem'}}>
                    {props.isLoading && <BarraCargando size="1.2em" />}
                    <ToggleField
                        label={t('datosContratosSection.public')}
                        name="datosContratos.publico"
                        register={form.register}
                    />
                </div>
            }
        >
            <SectionAlerts loadError={props.loadError}/>
            <div className="stackLg">

                <FormSection
                    title={t('datosContratosSection.majorContractsTitle', { count: contratosMayores.fields.length })}
                    collapsible
                    defaultOpen={false}
                    headerActions={!readOnly &&
                        <BulkToggle
                            label={t('datosContratosSection.public')}
                            fields={Array.isArray(currentContratosMayores) ? currentContratosMayores : []}
                            onToggle={setAllContratosMayoresPublic}
                        />
                    }
                >
                    {contratosMayores.fields.length === 0 && <EmptyState message={t('datosContratosSection.noMajorContracts')}/>}
                    {contratosMayores.fields.map((field, index) => (
                    <ContratoMayorFields
                        key={field.id}
                        proyectoId={props.proyectoId}
                            index={index}
                            control={form.control}
                            register={form.register}
                            setValue={form.setValue}
                            showIds={props.showIds}
                            onRemove={() => contratosMayores.remove(index)}
                        />
                    ))}

                    {!readOnly ? (
                        <button
                            className="btn btnSecondary"
                            type="button"
                            onClick={() =>
                                contratosMayores.append({
                                    id: undefined,
                                    numeroExpediente: '',
                                    descripcion: '',
                                    urlPliego: '',
                                    aprobacionInforme: '',
                                    aprobacionDecreto: '',
                                    pcspFechaPublicacion: '',
                                    pcspFechaLimitePresentacion: '',
                                    pcspMesaApertura: '',
                                    controlMinerva: '',
                                    numeroCLici: '',
                                    mesasPosteriores: '',
                                    plazosEjecucion: '',
                                    adjudicatario: undefined,
                                    proyectoSubcontratistas: [],
                                    publico: false,
                                })
                            }
                        >
                            {t('datosContratosSection.addMajorContract')}
                        </button>
                    ) : null}
                </FormSection>

                <PrivateContractField>
                <FormSection
                    title={t('datosContratosSection.minorContractsTitle', { count: contratosMenores.fields.length })}
                    collapsible
                    defaultOpen={false}
                    headerActions={!readOnly &&
                        <BulkToggle
                            label={t('datosContratosSection.public')}
                            fields={Array.isArray(currentContratosMenores) ? currentContratosMenores : []}
                            onToggle={setAllContratosMenoresPublic}
                        />
                    }
                >
                    {contratosMenores.fields.length === 0 && <EmptyState message={t('datosContratosSection.noMinorContracts')}/>}
                    {contratosMenores.fields.map((field, index) => (
                        <ContratoMenorFields
                            key={field.id}
                            index={index}
                            control={form.control}
                            register={form.register}
                            setValue={form.setValue}
                            showIds={props.showIds}
                            onRemove={() => contratosMenores.remove(index)}
                        />
                    ))}

                    {!readOnly ? (
                        <button
                            className="btn btnSecondary"
                            type="button"
                            onClick={() =>
                                contratosMenores.append({
                                    id: undefined,
                                    numeroExpediente: '',
                                    descripcion: '',
                                    adjudicatario: undefined,
                                    anexosPRTR_presentados: '',
                                    publico: false,
                                })
                            }
                        >
                            {t('datosContratosSection.addMinorContract')}
                        </button>
                    ) : null}
                </FormSection>

                <FormSection
                    title={t('datosContratosSection.expensesTitle', { count: gastos.fields.length })}
                    collapsible
                    defaultOpen={false}
                    headerActions={!readOnly &&
                        <BulkToggle
                            label={t('datosContratosSection.public')}
                            fields={Array.isArray(currentGastos) ? currentGastos : []}
                            onToggle={setAllGastosPublic}
                        />
                    }
                >
                    {gastos.fields.length === 0 && <EmptyState message={t('datosContratosSection.noExpenses')}/>}
                    {gastos.fields.map((field, index) => (
                        <ItemCard
                            key={field.id}
                            title={buildCardTitle(
                                t,
                                t('datosContratosSection.expense'),
                                index,
                                form.watch(`datosContratos.gastosImputadosFueraDeLaSubvencion.${index}.descripcion`),
                                form.watch(`datosContratos.gastosImputadosFueraDeLaSubvencion.${index}.documentoContable`),
                                formatSummaryNumber(form.watch(`datosContratos.gastosImputadosFueraDeLaSubvencion.${index}.importe`))
                            )}
                            summary={buildSummary(
                                t,
                                form.watch(`datosContratos.gastosImputadosFueraDeLaSubvencion.${index}.documentoContable`),
                                form.watch(`datosContratos.gastosImputadosFueraDeLaSubvencion.${index}.descripcion`),
                                formatSummaryNumber(form.watch(`datosContratos.gastosImputadosFueraDeLaSubvencion.${index}.importe`))
                            )}
                            onRemove={!readOnly ? () => gastos.remove(index) : undefined}
                            defaultOpen={!form.watch(`datosContratos.gastosImputadosFueraDeLaSubvencion.${index}.id`)}
                            actions={!readOnly &&
                                <ToggleField
                                    label={t('datosContratosSection.public')}
                                    name={`datosContratos.gastosImputadosFueraDeLaSubvencion.${index}.publico`}
                                    register={form.register}
                                />
                            }
                        >
                            <FieldGrid>
                                {showId(`datosContratos.gastosImputadosFueraDeLaSubvencion.${index}.id`) ? (
                                    <NumberField
                                        label={t('datosContratosSection.expense')}
                                        name={`datosContratos.gastosImputadosFueraDeLaSubvencion.${index}.id`}
                                        register={form.register}
                                    />
                                ) : null}
                                <TextField
                                    label={t('datosContratosSection.accountingDocument')}
                                    name={`datosContratos.gastosImputadosFueraDeLaSubvencion.${index}.documentoContable`}
                                    register={form.register}
                                />
                                <TextField
                                    label={t('datosContratosSection.description')}
                                    name={`datosContratos.gastosImputadosFueraDeLaSubvencion.${index}.descripcion`}
                                    register={form.register}
                                />
                                <CurrencyField
                                    label={t('datosContratosSection.amount')}
                                    name={`datosContratos.gastosImputadosFueraDeLaSubvencion.${index}.importe`}
                                    register={form.register}
                                />
                            </FieldGrid>

                            <label className="label">
                                {t('datosContratosSection.observations')}
                                <TextAreaField 
                                    label=""
                                    name={`datosContratos.gastosImputadosFueraDeLaSubvencion.${index}.observaciones`}
                                    register={form.register}
                                />
                            </label>
                        </ItemCard>
                    ))}

                    {!readOnly ? (
                        <button
                            className="btn btnSecondary"
                            type="button"
                            onClick={() =>
                                gastos.append({
                                    id: undefined,
                                    documentoContable: '',
                                    descripcion: '',
                                    importe: 0,
                                    observaciones: '',
                                    publico: false,
                                })
                            }
                        >
                            {t('datosContratosSection.addExpense')}
                        </button>
                    ) : null}
                </FormSection>
                </PrivateContractField>
            </div>
        </FormSection>
    );
}

function ContratoMayorFields(props: {
    proyectoId: number;
    index: number;
    register: UseFormRegister<ProyectoFormDTO>;
    control: Control<ProyectoFormDTO>;
    setValue: UseFormSetValue<ProyectoFormDTO>;
    showIds: boolean;
    onRemove: () => void;
}) {
    const { t } = useTranslation();
    const readOnly = useProjectReadOnly();
    const subcontratistas = useFieldArray({
        control: props.control,
        name: `datosContratos.contratoMayores.${props.index}.proyectoSubcontratistas` as const,
    });

    const idContratoMayor = useWatch({
        control: props.control,
        name: `datosContratos.contratoMayores.${props.index}.id` as const
    });
    const numeroExpediente = useWatch({
        control: props.control,
        name: `datosContratos.contratoMayores.${props.index}.numeroExpediente` as const
    });
    const descripcion = useWatch({
        control: props.control,
        name: `datosContratos.contratoMayores.${props.index}.descripcion` as const
    });
    const fechaPublicacion = useWatch({
        control: props.control,
        name: `datosContratos.contratoMayores.${props.index}.pcspFechaPublicacion` as const
    });

    const setAllSubcontratistasPublic = (isPublic: boolean) => {
        subcontratistas.fields.forEach((field, index) => {
            props.setValue(`datosContratos.contratoMayores.${props.index}.proyectoSubcontratistas.${index}.publico`, isPublic, { shouldDirty: true });
        });
    };

    const currentSubcontratistas = useWatch({
        control: props.control,
        name: `datosContratos.contratoMayores.${props.index}.proyectoSubcontratistas` as any
    }) || [];

    return (
        <ItemCard
            title={descripcion || buildCardTitle(t, t('datosContratosSection.majorContract'), props.index)}
            summary={readOnly ? buildSummary(t, descripcion) : buildSummary(t, numeroExpediente, descripcion, fechaPublicacion)}
            onRemove={!readOnly ? props.onRemove : undefined}
            defaultOpen={!idContratoMayor || idContratoMayor === 0}
            actions={!readOnly &&
                <ToggleField
                    label={t('datosContratosSection.public')}
                    name={`datosContratos.contratoMayores.${props.index}.publico`}
                    register={props.register}
                />
            }
        >
            <FieldGrid>
                {props.showIds && hasPersistedId(idContratoMayor) ? (
                    <NumberField label="ID" name={`datosContratos.contratoMayores.${props.index}.id`}
                                 register={props.register}/>
                ) : null}
                <PrivateContractField>
                    <TextField label={t('datosContratosSection.recordNumber')}
                               name={`datosContratos.contratoMayores.${props.index}.numeroExpediente`}
                               register={props.register}/>
                </PrivateContractField>
                <TextField label={t('datosContratosSection.contractName')} name={`datosContratos.contratoMayores.${props.index}.descripcion`}
                           register={props.register}/>
                <SpecificationLinkField index={props.index} register={props.register}/>
                <PrivateContractField><DateField label={t('datosContratosSection.reportApproval')} name={`datosContratos.contratoMayores.${props.index}.aprobacionInforme`} register={props.register}/></PrivateContractField>
                <PrivateContractField><DateField label={t('datosContratosSection.decreeApproval')} name={`datosContratos.contratoMayores.${props.index}.aprobacionDecreto`} register={props.register}/></PrivateContractField>
                <PrivateContractField><DateField label={t('datosContratosSection.pcspPublicationDate')} name={`datosContratos.contratoMayores.${props.index}.pcspFechaPublicacion`} register={props.register}/></PrivateContractField>
                <PrivateContractField><DateField label={t('datosContratosSection.pcspDeadline')} name={`datosContratos.contratoMayores.${props.index}.pcspFechaLimitePresentacion`} register={props.register}/></PrivateContractField>
                <PrivateContractField><DateField label={t('datosContratosSection.pcspOpeningMeeting')} name={`datosContratos.contratoMayores.${props.index}.pcspMesaApertura`} register={props.register}/></PrivateContractField>
                <PrivateContractField><DateField label={t('datosContratosSection.controlMinerva')} name={`datosContratos.contratoMayores.${props.index}.controlMinerva`} register={props.register}/></PrivateContractField>
                <PrivateContractField><TextAreaField label={t('datosContratosSection.cliciNumber')} name={`datosContratos.contratoMayores.${props.index}.numeroCLici`} register={props.register}/></PrivateContractField>
                <PrivateContractField><TextAreaField label={t('datosContratosSection.subsequentMeetings')} name={`datosContratos.contratoMayores.${props.index}.mesasPosteriores`} register={props.register}/></PrivateContractField>
                <PrivateContractField><TextAreaField label={t('datosContratosSection.executionDeadlines')} name={`datosContratos.contratoMayores.${props.index}.plazosEjecucion`} register={props.register}/></PrivateContractField>
            </FieldGrid>

            <PrivateContractField><NestedPanel title={t('datosContratosSection.awardee')}>
                <AdjudicatarioManager
                    baseName={`datosContratos.contratoMayores.${props.index}.adjudicatario`}
                    contractBaseName={`datosContratos.contratoMayores.${props.index}`}
                    control={props.control}
                    register={props.register}
                    setValue={props.setValue}
                    showIds={props.showIds}
                />
            </NestedPanel></PrivateContractField>

            <PrivateContractField><NestedPanel
                title={t('datosContratosSection.subcontractors')}
                actions={!readOnly &&
                    <BulkToggle
                        label={t('datosContratosSection.public')}
                        fields={Array.isArray(currentSubcontratistas) ? currentSubcontratistas : []}
                        onToggle={setAllSubcontratistasPublic}
                    />
                }
            >
                {subcontratistas.fields.length === 0 && <EmptyState message={t('datosContratosSection.noSubcontractors')}/>}
                {subcontratistas.fields.map((field, subIndex) => (
                    <SubcontratistaFields
                        key={field.id}
                        proyectoId={props.proyectoId}
                        parentIndex={props.index}
                        index={subIndex}
                        control={props.control}
                        register={props.register}
                        showIds={props.showIds}
                        onRemove={() => subcontratistas.remove(subIndex)}
                    />
                ))}

                {!readOnly ? (
                    <button className="btn btnGhost" type="button"
                            onClick={() => subcontratistas.append({id: undefined, nombre: '', documentacion: [], publico: false})}>
                        {t('datosContratosSection.addSubcontractor')}
                    </button>
                ) : null}
            </NestedPanel></PrivateContractField>
        </ItemCard>
    );
}

function PrivateContractField(props: { children: React.ReactNode }) {
    const publicView = useProjectPublicView();
    return publicView ? null : <>{props.children}</>;
}

function SpecificationLinkField(props: { index: number; register: UseFormRegister<ProyectoFormDTO> }) {
    const { t } = useTranslation();
    const readOnly = useProjectReadOnly();
    const form = useFormContext<ProyectoFormDTO>();
    const path = `datosContratos.contratoMayores.${props.index}.urlPliego` as const;
    const value = useWatch({control: form.control, name: path}) as string | undefined;

    if (readOnly) {
        if (!value) return null;
        const href = value.startsWith('http') ? value : `https://${value}`;
        return (
            <div className="label">
                {t('datosContratosSection.specificationUrl')}
                <a className="input" href={href} target="_blank" rel="noopener noreferrer">
                    {value}
                </a>
            </div>
        );
    }

    return <TextField label={t('datosContratosSection.specificationUrl')} name={path} register={props.register}/>;
}

function ContratoMenorFields(props: {
    index: number;
    register: UseFormRegister<ProyectoFormDTO>;
    control: Control<ProyectoFormDTO>;
    setValue: UseFormSetValue<ProyectoFormDTO>;
    showIds: boolean;
    onRemove: () => void;
}) {
    const { t } = useTranslation();
    const readOnly = useProjectReadOnly();
    const idContratoMenor = useWatch({
        control: props.control,
        name: `datosContratos.contratoMenores.${props.index}.id` as const
    });
    const numeroExpediente = useWatch({
        control: props.control,
        name: `datosContratos.contratoMenores.${props.index}.numeroExpediente` as const
    });
    const descripcion = useWatch({
        control: props.control,
        name: `datosContratos.contratoMenores.${props.index}.descripcion` as const
    });
    const fechaInicioContrato = useWatch({
        control: props.control,
        name: `datosContratos.contratoMenores.${props.index}.fechaInicioContrato` as const
    });

    return (
        <ItemCard
            title={buildCardTitle(t, t('datosContratosSection.minorContract'), props.index, descripcion, numeroExpediente, fechaInicioContrato)}
            summary={buildSummary(t, numeroExpediente, descripcion, fechaInicioContrato)}
            onRemove={!readOnly ? props.onRemove : undefined}
            defaultOpen={!idContratoMenor || idContratoMenor === 0}
            actions={!readOnly &&
                <ToggleField
                    label={t('datosContratosSection.public')}
                    name={`datosContratos.contratoMenores.${props.index}.publico`}
                    register={props.register}
                />
            }
        >
            <FieldGrid>
                {props.showIds && hasPersistedId(idContratoMenor) ? (
                    <NumberField label="ID" name={`datosContratos.contratoMenores.${props.index}.id`}
                                 register={props.register}/>
                ) : null}
                <TextField label={t('datosContratosSection.recordNumber')}
                           name={`datosContratos.contratoMenores.${props.index}.numeroExpediente`}
                           register={props.register}/>
                <TextField label={t('datosContratosSection.description')} name={`datosContratos.contratoMenores.${props.index}.descripcion`}
                           register={props.register}/>
                <DateField label={t('datosContratosSection.motivatingReport')}
                           name={`datosContratos.contratoMenores.${props.index}.informeMotivandoNecesidad`}
                           register={props.register}/>
                <DateField label={t('datosContratosSection.minerva')} name={`datosContratos.contratoMenores.${props.index}.minerva`}
                           register={props.register}/>
                <DateField label={t('datosContratosSection.resolutionProposalReport')}
                           name={`datosContratos.contratoMenores.${props.index}.informePropuestaResolucion`}
                           register={props.register}/>
                <DateField label={t('datosContratosSection.awardDecree')}
                           name={`datosContratos.contratoMenores.${props.index}.decretoDeAdjudicacion`}
                           register={props.register}/>
                <DateField label={t('datosContratosSection.contractStartDate')}
                           name={`datosContratos.contratoMenores.${props.index}.fechaInicioContrato`}
                           register={props.register}/>
                <DateField label={t('datosContratosSection.initialEstimateDate')}
                           name={`datosContratos.contratoMenores.${props.index}.fechaEstimacionInicial`}
                           register={props.register}/>
                <DateField label={t('datosContratosSection.maxCompletionDate')}
                           name={`datosContratos.contratoMenores.${props.index}.fechaMaximaFinalizacionContrato`}
                           register={props.register}/>
                <TextField label={t('datosContratosSection.prtrAnnexes')}
                           name={`datosContratos.contratoMenores.${props.index}.anexosPRTR_presentados`}
                           register={props.register}/>
            </FieldGrid>

            <NestedPanel title={t('datosContratosSection.awardee')}>
                <AdjudicatarioManager
                    baseName={`datosContratos.contratoMenores.${props.index}.adjudicatario`}
                    contractBaseName={`datosContratos.contratoMenores.${props.index}`}
                    control={props.control}
                    register={props.register}
                    setValue={props.setValue}
                    showIds={props.showIds}
                />
            </NestedPanel>
        </ItemCard>
    );
}

function AdjudicatarioManager(props: {
    baseName: string;
    contractBaseName: string;
    control: Control<ProyectoFormDTO>;
    register: UseFormRegister<ProyectoFormDTO>;
    setValue: UseFormSetValue<ProyectoFormDTO>;
    showIds: boolean;
}) {
    const { t } = useTranslation();
    const { control, setValue, baseName, contractBaseName } = props;
    const readOnly = useProjectReadOnly();
    const adjudicatario = useWatch({ control, name: baseName as any }) as ProyectoAdjudicatarioDTO | undefined;

    const contratoNumeroExpediente = useWatch({ control, name: `${contractBaseName}.numeroExpediente` as any });
    const contratoNumeroCLici = useWatch({ control, name: `${contractBaseName}.numeroCLici` as any });

    const handleCreate = () => {
        setValue(baseName as any, {
            id: 0,
            numeroExpediente: String(contratoNumeroExpediente ?? ''),
            numeroC_LICI: String(contratoNumeroCLici ?? ''),
            nombre: '',
            informacionContactos: '',
            documentacion: createDefaultDocumentacionAdjudicatario(),
            publico: false,
        }, { shouldDirty: true });
    };

    const isPersisted = hasPersistedId(adjudicatario?.id);
    const isUserDataEmpty = !adjudicatario?.nombre && !adjudicatario?.informacionContactos;

    const showEmptyState = !adjudicatario || (isPersisted && isUserDataEmpty);

    if (showEmptyState) {
        return (
            <div className="stack">
                <EmptyState message={t('datosContratosSection.noAwardees')} />
                {!readOnly && (
                    <button type="button" className="btn btnSecondary" onClick={handleCreate}>
                        {t('datosContratosSection.addAwardee')}
                    </button>
                )}
            </div>
        );
    }

    return <AdjudicatarioFields {...props} adjudicatario={adjudicatario} />;
}

function AdjudicatarioFields(props: {
    baseName: string;
    contractBaseName: string;
    control: Control<ProyectoFormDTO>;
    register: UseFormRegister<ProyectoFormDTO>;
    setValue: UseFormSetValue<ProyectoFormDTO>;
    showIds: boolean;
    adjudicatario: any; // El objeto adjudicatario
}) {
    const { t } = useTranslation();
    const { baseName, control, register, setValue, showIds, adjudicatario } = props;
    const readOnly = useProjectReadOnly();
    const isAdminArea = window.location.pathname.includes('/administracion');
    const isMenor = props.contractBaseName.includes('contratoMenores');

    const docs = useFieldArray({
        control: control,
        name: `${baseName}.documentacion` as any,
    });

    const setAllDocsPublic = (isPublic: boolean) => {
        docs.fields.forEach((field, index) => {
            setValue(`${baseName}.documentacion.${index}.publico` as any, isPublic, { shouldDirty: true });
        });
    };

    const currentDocs = useWatch({ control, name: `${baseName}.documentacion` as any }) || [];

    return (
        <div className="stack">
            <FieldGrid>
                <TextField label={t('datosContratosSection.awardeeName')} name={`${baseName}.nombre`} register={register}/>
                
                {showIds && hasPersistedId(adjudicatario.id) ? (
                    <NumberField label={t('datosContratosSection.awardeeId')} name={`${baseName}.id`} register={register}/>
                ) : null}
                <TextField
                    label={t('datosContratosSection.awardeeRecordNumber')}
                    name={`${baseName}.numeroExpediente`}
                    register={register}
                />

                {!isMenor && (
                    <TextField
                        label={t('datosContratosSection.awardeeCliciNumber')}
                        name={`${baseName}.numeroC_LICI`}
                        register={register}
                    />
                )}
            </FieldGrid>

            <label className="label">
                {t('datosContratosSection.contactInfo')}
                <TextAreaField 
                    label=""
                    name={`${baseName}.informacionContactos`}
                    register={register}
                />
            </label>

            {isAdminArea && (
                <NestedPanel
                    title={t('datosContratosSection.awardeeDocumentation')}
                    actions={!readOnly &&
                        <BulkToggle
                            label={t('datosContratosSection.public')}
                            fields={Array.isArray(currentDocs) ? currentDocs : []}
                            onToggle={setAllDocsPublic}
                        />
                    }
                >
                    {docs.fields.length === 0 && <EmptyState message={t('datosContratosSection.noDocuments')}/>}
                    {docs.fields.map((field, docIndex) => (
                        <DocumentacionAdjudicatarioFields
                            key={field.id}
                            baseName={baseName}
                            index={docIndex}
                            control={control}
                            register={register}
                            showIds={showIds}
                            onRemove={() => docs.remove(docIndex)}
                        />
                    ))}

                    {!readOnly && (
                        <button
                            className="btn btnGhost"
                            type="button"
                            onClick={() =>
                                docs.append({
                                    id: undefined,
                                    nombreDocumento: '',
                                    presentado: false,
                                    observaciones: '',
                                    publico: false,
                                })
                            }
                        >
                            {t('datosContratosSection.addDocument')}
                        </button>
                    )}
                </NestedPanel>
            )}
        </div>
    );
}

function SubcontratistaFields(props: {
    proyectoId: number;
    parentIndex: number;
    index: number;
    control: Control<ProyectoFormDTO>;
    register: UseFormRegister<ProyectoFormDTO>;
    showIds: boolean;
    onRemove: () => void;
}) {
    const { t } = useTranslation();
    const readOnly = useProjectReadOnly();
    const isAdminArea = window.location.pathname.startsWith('/administracion');
    const idSubcontratista = useWatch({
        control: props.control,
        name: `datosContratos.contratoMayores.${props.parentIndex}.proyectoSubcontratistas.${props.index}.id` as const,
    });

    const nombre = useWatch({
        control: props.control,
        name: `datosContratos.contratoMayores.${props.parentIndex}.proyectoSubcontratistas.${props.index}.nombre` as const,
    });

    const baseName = `datosContratos.contratoMayores.${props.parentIndex}.proyectoSubcontratistas.${props.index}`;

    return (
        <ItemCard
            title={buildCardTitle(t, t('datosContratosSection.subcontractor'), props.index, nombre)}
            summary={buildSummary(t, nombre)}
            onRemove={!readOnly ? props.onRemove : undefined}
            compact
            defaultOpen={!idSubcontratista || idSubcontratista === 0}
            actions={!readOnly &&
                <ToggleField
                    label={t('datosContratosSection.public')}
                    name={`${baseName}.publico`}
                    register={props.register}
                />
            }
        >
            <FieldGrid>
                <TextField label={t('datosContratosSection.subcontractorName')} name={`${baseName}.nombre`} register={props.register}/>
                {props.showIds && hasPersistedId(idSubcontratista) ?
                    <NumberField label={t('datosContratosSection.subcontractorId')} name={`${baseName}.id`} register={props.register}/> : null}
            </FieldGrid>

            {isAdminArea && (
                <NestedPanel title={t('datosContratosSection.subcontractorDocumentation')}>
                    <DocumentosAdjuntosPanel
                        proyectoId={props.proyectoId}
                        ambito="SUBCONTRATISTA"
                        subcontratistaId={hasPersistedId(idSubcontratista) ? idSubcontratista : undefined}
                        disabled={!hasPersistedId(idSubcontratista)}
                    />
                </NestedPanel>
            )}
        </ItemCard>
    );
}

function DocumentacionAdjudicatarioFields(props: {
    baseName: string;
    index: number;
    control: Control<ProyectoFormDTO>;
    register: UseFormRegister<ProyectoFormDTO>;
    showIds: boolean;
    onRemove: () => void;
}) {
    const { t } = useTranslation();
    const readOnly = useProjectReadOnly();
    const documentId = useWatch({
        control: props.control,
        name: `${props.baseName}.documentacion.${props.index}.id` as any
    });
    const nombreDocumento = useWatch({
        control: props.control,
        name: `${props.baseName}.documentacion.${props.index}.nombreDocumento` as any
    });
    const presentado = useWatch({
        control: props.control,
        name: `${props.baseName}.documentacion.${props.index}.presentado` as any
    });

    return (
        <ItemCard
            title={buildCardTitle(t, t('datosContratosSection.document'), props.index, nombreDocumento)}
            summary={buildSummary(t, nombreDocumento)}
            badge={<StatusPill label={presentado ? t('datosContratosSection.submitted') : t('datosContratosSection.pending')} tone={getStatusTone(presentado)}/>}
            compact
            onRemove={!readOnly ? props.onRemove : undefined}
            defaultOpen={!documentId || documentId === 0}
            actions={!readOnly &&
                <ToggleField
                    label={t('datosContratosSection.public')}
                    name={`${props.baseName}.documentacion.${props.index}.publico`}
                    register={props.register}
                />
            }
        >
            <FieldGrid>
                <TextField label={t('datosContratosSection.documentName')}
                           name={`${props.baseName}.documentacion.${props.index}.nombreDocumento`}
                           register={props.register}/>
                {props.showIds && hasPersistedId(documentId) ? (
                    <NumberField label={t('datosContratosSection.documentId')} name={`${props.baseName}.documentacion.${props.index}.id`}
                                 register={props.register}/>
                ) : null}
            </FieldGrid>
            
            <label className="label">
                {t('datosContratosSection.observations')}
                <TextAreaField 
                    label=""
                    name={`${props.baseName}.documentacion.${props.index}.observaciones`}
                    register={props.register}
                />
            </label>

            <CheckboxField label={t('datosContratosSection.submitted')} name={`${props.baseName}.documentacion.${props.index}.presentado`}
                           register={props.register}/>
        </ItemCard>
    );
}
